import clientData from '../../../assets/data/client-types.json'

export const CLIENT_TYPES = clientData.clientTypes
export const CURRICULUM_OPTIONS = clientData.curriculumOptions
export const CLASS_OPTIONS = clientData.classOptions
export const BUDGET_UNIT_OPTIONS = clientData.budgetUnitOptions
export const BOARD_OPTIONS = clientData.boardOptions
export const STATE_OPTIONS = clientData.stateOptions
export const INTERESTED_PRODUCT_OPTIONS = clientData.interestedProductOptions

export const ALL_GRADES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

export const PRODUCT_DISPLAY_LABELS = {
    SHIKSHA_ASSESSMENTS: 'Shiksha Assessments',
} as const

export const SOURCE_OPTIONS = clientData.sourceOptions
export const ROLES = clientData.roles
export const ENGAGEMENT_TYPE_OPTIONS = ['Inbound', 'Outbound']
export const UNASSIGNED_CLIENTS_PAGINATION = clientData.unassignedClients

// Communication/Contacts placeholders
export const CONTACT_PLACEHOLDER_NAME = 'Unknown Contact'
export const CONTACT_PLACEHOLDER_ROLE = 'Contact Person'
/** Display placeholder when phone or email is not provided (customer creation / contact lists). */
export const CONTACT_PLACEHOLDER_NOT_AVAILABLE = 'NA'
export const CONTACT_PLACEHOLDER_MOBILE = CONTACT_PLACEHOLDER_NOT_AVAILABLE
export const CONTACT_PLACEHOLDER_EMAIL = CONTACT_PLACEHOLDER_NOT_AVAILABLE
export const CONTACT_ROLE_KEYWORDS = {
    PRINCIPAL: 'principal',
    VICE: 'vice',
} as const

/**
 * Lowercase tokens treated as empty/missing for contact display and edit flows
 * (dash variants, N/A spellings, “not available”, etc.).
 */
const CONTACT_DISPLAY_EMPTY_VALUE_TOKENS_LOWERCASE = [
    '-',
    '—',
    'n/a',
    'na',
    'n.a',
    'not available',
    'not specified',
    'n\\a',
] as const

export function isContactDisplayEmptyValueToken(lowerTrimmed: string): boolean {
    return (CONTACT_DISPLAY_EMPTY_VALUE_TOKENS_LOWERCASE as readonly string[]).includes(
        lowerTrimmed,
    )
}

export const CONTACT_FORM_FIELDS = {
    IS_POC: 'isPOC',
} as const

// Add-contact helper UI strings
export const CONTACT_HELPER_TEXT = 'Unable to find the contact?'
export const CONTACT_HELPER_ADD_LABEL = 'Add new contact'

// Communication placeholders - dropdowns
export const CONTACT_EMAIL_SELECT_PLACEHOLDER = 'Select an email'
export const CALL_STATUS_SELECT_PLACEHOLDER = 'Select Status'
export const CONTACT_SELECT_PLACEHOLDER = 'Select Contact'
export const CONTACT_MEETING_PARTICIPANT_SELECT_PLACEHOLDER = 'Select meeting participants'

// Contact refresh events
export const CONTACT_REFRESH_EVENT = 'contact-refresh-triggered'

export const NO_CONTACTS_FOUND_MESSAGE = 'No contacts found'

/** First page index for orchestrate organisation contacts list (`/orchestrate/organisations/contacts`). */
export const ORGANISATION_CONTACTS_LIST_DEFAULT_PAGE = 1

/** Page size for orchestrate organisation contacts list (`/orchestrate/organisations/contacts`). */
export const ORGANISATION_CONTACTS_LIST_PAGE_LIMIT = 100

// Default country code (India)
export const DEFAULT_COUNTRY_CODE = 91

// Client type values for API requests
export const CLIENT_TYPE_VALUES = {
    FUNDER: 'funder',
} as const

// Sidebar: Create Client menu labels
export const CREATE_CLIENT_MENU = {
    TITLE: 'Create Client',
    SUBMENU_SINGLE: 'Single',
    SUBMENU_BULK: 'Bulk',
} as const

// CRM route path segments used in navigation
export const CRM_ROUTE_PATHS = {
    CREATE: 'create',
    BULK_UPLOAD: 'bulk-upload',
    BASE: '/crm',
    VIEW_CLIENT: '/crm/dashboard',
    VIEW_CLIENT_SHIKSHA: '/crm/view-client-shiksha',
    RETENTION_APPROVAL: 'retention-approval',
} as const

// Error messages
export const ERROR_MESSAGES = {
    ORGANIZATION_CODE_MISSING: 'Customer code missing in response',
    FUNDER_ID_MISSING: 'Funder ID missing in response',
    FAILED_TO_SAVE_ADDRESS: 'Failed to save customer address',
    FAILED_TO_RECORD_MEETING: 'Failed to record meeting. Please try again.',
} as const

/** Contact step — National School (sales) mobile; Shiksha/funder keeps7–15 intl. rules */
export const NATIONAL_SALES_MOBILE_INVALID_MESSAGE =
    'Enter a valid 10-digit mobile number starting with 6, 7, 8, or 9.'

/** Contact step — Shiksha / international create-client mobile (7–15 digits). */
export const INTERNATIONAL_CONTACT_MOBILE_INVALID_MESSAGE =
    'Please enter a valid mobile number (7–15 digits)'

/** Website field copy — Client Information step (create client). */
export const CLIENT_INFORMATION_WEBSITE = {
    OPTIONAL_SCHEME_PLACEHOLDER: 'www.example.com',
    TOAST_INVALID_FUNDER:
        'Website must be a full URL starting with https:// (e.g. https://www.example.com).',
    TOAST_INVALID_NON_FUNDER: 'Enter a valid website (e.g. www.school.edu).',
    /** National School only — Shiksha funders keep TOAST_INVALID_FUNDER. */
    TOAST_INVALID_NATIONAL_WWW:
        'Enter a valid website starting with www. It must end with .com, .edu, or .co.in (e.g. www.school.com, www.school.edu, www.school.co.in).',
    FORM_PATTERN_ERROR_FUNDER:
        'Enter a full website URL starting with https:// (e.g. https://www.example.com). http:// or links without https are not accepted.',
    FORM_PATTERN_ERROR_NON_FUNDER: 'Enter a valid website (e.g. www.school.edu or school.ac.in).',
    /** National School only — requires www. prefix and no http(s)://. */
    FORM_PATTERN_ERROR_NATIONAL_WWW:
        'Enter a valid website starting with www. It must end with .com, .edu, or .co.in (e.g. www.school.com).',
    FORM_REQUIRED_ERROR: 'Website cannot be empty.',
    FORM_MAX_LENGTH_ERROR: 'Website URL is too long.',
} as const

// Success messages
export const SUCCESS_MESSAGES = {
    CUSTOMER_CREATED: 'Customer Created Successfully!',
    FUNDER_CREATED: 'Funder Created Successfully!',
    CLIENT_CREATED: 'Customer created successfully',
    /** Shown under CLIENT_CREATED for National School create-client flow */
    CLIENT_CREATED_NATIONAL_LOGISTICS_REVIEW:
        'Customer information has been sent to the logistics team for review',
} as const

// Dialog titles
export const DIALOG_TITLES = {
    CUSTOMER_CREATED: 'Customer Created',
    FUNDER_CREATED: 'Funder Created',
    ERROR: 'Error',
    ADD_CLIENT: 'Add Client',
} as const

// Dialog messages
export const DIALOG_MESSAGES = {
    ADD_CLIENT: {
        title: 'Add Client',
        message:
            "You've completed all the mandatory details for this client. You can add the client now, or continue entering additional information such as Contact Details and Academic Information.",
        confirmText: 'Yes, Add Client',
        cancelText: 'I want to add more details',
    },
} as const

export const DEMO_FEEDBACK_FILE_CONSTANTS = {
    // Demo feedback file upload constants
    ACCEPTED_FILE_TYPES: [
        '.pdf',
        '.png',
        '.jpg',
        '.jpeg',
        '.xlsx',
        '.xls',
        '.doc',
        '.docx',
    ] as const,
    MAX_FILE_SIZE_BYTES: 5 * 1024 * 1024, // 5MB
    MAX_FILE_SIZE_MB: 5,
    DISPLAY_TEXT: {
        FILE_TYPES: 'PDF, PNG, Excel, Word, JPG',
        MAX_SIZE: '5MB',
    },
} as const

export const DUPLICATE_WEIGHTS = {
    PHONE: 4,
    NAME: 3,
    ADDRESS: 2,
    POC_NAME: 1,
    EMAIL: 4,
    PINCODE: 1,
    DOMAIN: 2,
} as const

// Lead stage numbers
export const LEAD_STAGE_NUMBERS = {
    S0: 0,
    S1: 1,
    S2: 2,
    S3: 3,
    S4: 4,
    S5: 5,
    S6: 6,
    S7: 7,
} as const

// Default funder type
export const DEFAULT_FUNDER_TYPE = 'corporate_foundation' as const

// Empty search response constant
export const EMPTY_SEARCH_RESPONSE = {
    success: false,
    message: '',
    data: {
        results: [],
        pagination: {
            total: 0,
            page: 1,
            limit: 10,
        },
    },
}

// Client View Page Constants
export const CLIENT_VIEW_PAGE_CONSTANTS = {
    PAGE_TITLE: 'Client Details',
    LEADS_TAB_LABEL: 'Leads',
    DETAILS_TAB_LABEL: 'Details',
    NO_LEADS_MESSAGE: 'No leads found for this client',
    LOADING_MESSAGE: 'Loading customer information...',
} as const

export const DEFAULT_PAGE_NUMBER = 1
export const DEFAULT_PAGE_SIZE = 10
